import { format, parse } from 'date-fns'
import { EventAttendanceModeType, EventJsonLdData, generateEventJsonLd } from '~/lib/utils'
import { EventFieldsFragment, TaxonomyTermInstitutionSector } from '~/api/graphql/generated'

function parseTimeString(timeStr?: string): string[] | undefined {
  if (!timeStr) return undefined
  // Normalize the input by replacing different dash types and trimming spaces
  const normalizedTimeStr = timeStr.replace('–', '-').replace('—', '-').trim()
  // First handle the case with "Uhr" and additional text
  if (normalizedTimeStr.match(/^\d{1,2}[.:]\d{2}\s*-\s*\d{1,2}[.:]\d{2}\s*Uhr/)) {
    const timeRange = normalizedTimeStr.split('Uhr')[0].trim()
    return timeRange.split('-').map((time) => time.trim())
  }
  // Then handle the basic format
  if (normalizedTimeStr.match(/^\d{1,2}[.:]\d{2}\s*-\s*\d{1,2}[.:]\d{2}/)) {
    return normalizedTimeStr.split('-').map((time) => time.trim())
  }
  return undefined
}

/**
 * Composable for adding schema.org JSON-LD structured data to event pages
 * @param entity The Drupal event entity object
 */
export const useSchemaEvent = (entity: EventFieldsFragment | undefined) => {
  if (!entity) return

  // Format dates to ISO format for schema.org
  const startDate = entity.fieldStartDate?.date
    ? parse(entity.fieldStartDate.date, "yyyy-MM-dd HH:mm:ss 'UTC'", new Date())
    : new Date()

  // Add format to YYYY-MM-DD
  let isoStartDate = format(startDate, 'yyyy-MM-dd')

  // Process optional end date if available
  let isoEndDate
  if (entity.fieldEndDate?.date) {
    const endDate = parse(entity.fieldEndDate.date, "yyyy-MM-dd HH:mm:ss 'UTC'", new Date())
    isoEndDate = format(endDate, 'yyyy-MM-dd')
  } else {
    // If no end date is provided, use the start date as the end date
    isoEndDate = isoStartDate
  }

  const timeStrings = parseTimeString(entity?.fieldTimeWeekday || undefined)
  const startTimeString = timeStrings ? timeStrings[0].replace('.', ':') : '00:00'
  const endTimeString = timeStrings ? timeStrings[1].replace('.', ':') : '23:59'
  if (startTimeString && endTimeString) {
    isoStartDate = `${isoStartDate}T${startTimeString}`
    isoEndDate = `${isoEndDate}T${endTimeString}`
  }
  // Extract institution information
  const fieldInstitution = entity.fieldInstitution?.entity as TaxonomyTermInstitutionSector | undefined
  const institutionName = fieldInstitution?.name || 'Careum'

  // Extract location data from address fields
  const location = {
    name: institutionName,
    address: entity.fieldAddress
      ? {
          streetAddress: entity.fieldAddress.addressLine1 || undefined,
          addressLocality: entity.fieldAddress.locality || undefined,
          postalCode: entity.fieldAddress.postalCode || undefined,
          addressCountry: 'CH',
        }
      : undefined,
  }

  // Extract image URL if available
  let imageUrl
  if (entity.fieldImage?.entity) {
    // @ts-expect-error Accessing Drupal generated entity url if present
    imageUrl = entity.fieldImage.entity.url
  }

  // Create the event data object
  const eventData: EventJsonLdData = {
    name: entity.title || 'Event',
    startDate: isoStartDate,
    endDate: isoEndDate,
    location,
    image: imageUrl,
    // field Lead should be stripped of HTML tags.
    description: entity.fieldLead?.processed?.replace(/<[^>]*>?/g, '') || undefined,
    eventAttendanceMode: entity.fieldPhysical ? EventAttendanceModeType.Offline : EventAttendanceModeType.Online,
  }

  // Add offers price information if the fieldPriceSchema is not null.
  if (entity.fieldPriceSchema !== null) {
    eventData.offers = {
      price: entity.fieldPriceSchema,
      priceCurrency: 'CHF',
    }
  }
  // Add free accessibility flag if priceSchema is 0.00.
  if (entity.fieldPriceSchema === '0.00') {
    eventData.isAccessibleForFree = true
  }

  // Generate the JSON-LD and add it to the page head
  const jsonLd = generateEventJsonLd(eventData)

  useHead({
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify(jsonLd),
      },
    ],
  })
}
